import { computed, inject, Injectable, signal } from '@angular/core';
import { makeEvent } from '../analytics/builder';
import { AssetsStore } from '../stores/assets.store';
import { AuthStore } from '../stores/auth.store';
import { FeatureFlagStore } from '../stores/feature-flags.store';
import { askAssetQuestion, ChatErrorKind, ChatRequestError, ChatResponse } from './api/chat';
import { AuthBridgeService } from './auth-bridge';
import { formatAnalysisText } from './format-analysis-text';
import { SnowplowService } from './snowplow';
import { ThemeService } from './theme';
import { v3GuestMode } from './v3-guest-mode';

// The backend's CHAT_MAX_QUESTION_CHARS default.
export const CHAT_MAX_QUESTION_CHARS = 500;

// Chips shown before the first question (AI-618's test questions).
export const CHAT_SUGGESTED_QUESTIONS: readonly string[] = [
  'Was ist die Rule of 40 und wie steht das Unternehmen da?',
  'Wie hat sich das Umsatzwachstum in den letzten 5 Jahren entwickelt?',
  'Was sagen die Analysten aktuell?',
  'Wie schneidet das Unternehmen im Vergleich zu den Wettbewerbern ab?',
];

export type ChatQuestionSource = 'typed' | 'suggested' | 'follow_up';

// One question and its answer; the backend is stateless, so exchanges are independent.
export interface ChatExchange {
  id: number;
  question: string;
  source: ChatQuestionSource;
  status: 'pending' | 'done' | 'error';
  response: ChatResponse | null;
  html: string;
  error: ChatErrorKind | null;
}

// Reduces the markdown answer to what formatAnalysisText renders (no markdown library in the app).
export function renderChatAnswer(markdown: string): string {
  const text = (markdown ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/^#{1,6}\s+(.+?)\s*#*\s*$/gm, '**$1**')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/^\s*[-*•]\s+/gm, '- ')
    .replace(/^\s*(\d+)[.)]\s+/gm, '$1. ')
    // "6,4 %" → "6,4%": the app's own convention, and the sign can't wrap onto its own line.
    .replace(/(\d)\s+(%|‰)/g, '$1$2');
  return formatAnalysisText(renderTables(text));
}

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEPARATOR = /^\s*\|(\s*:?-+:?\s*\|)+\s*$/;

// Pipe tables (header, `|---|---:|` separator, rows) become one-line <table> markup, which the
// line-based formatting after it leaves alone; the bold inside cells still gets converted.
function renderTables(text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (!TABLE_ROW.test(lines[i]) || !TABLE_SEPARATOR.test(lines[i + 1] ?? '')) {
      out.push(lines[i]);
      continue;
    }
    const header = splitTableRow(lines[i]);
    const aligns = splitTableRow(lines[i + 1]).map((c) =>
      c.startsWith(':') && c.endsWith(':') ? 'center' : c.endsWith(':') ? 'right' : '',
    );
    const rows: string[][] = [];
    let next = i + 2;
    while (next < lines.length && TABLE_ROW.test(lines[next]))
      rows.push(splitTableRow(lines[next++]));
    i = next - 1;

    const cell = (tag: string, value: string | undefined, col: number) =>
      `<${tag}${aligns[col] ? ` class="${aligns[col]}"` : ''}>${value ?? ''}</${tag}>`;
    const row = (tag: string, cells: string[]) =>
      `<tr>${header.map((_, col) => cell(tag, cells[col], col)).join('')}</tr>`;
    out.push(
      `<div class="tableWrap"><table class="chatTable"><thead>${row('th', header)}</thead>` +
        `<tbody>${rows.map((cells) => row('td', cells)).join('')}</tbody></table></div>`,
    );
  }
  return out.join('\n');
}

function splitTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
}

// Context-block id prefixes (backend/app/chat/retrieve.py, `ai_analysis:<AnalysisType>:<isin>`) →
// "Quellen" labels, named after the tab the analysis feeds. No colon where a prefix covers
// several types (earnings_report_v2 + earnings_transcripts_v2, analysts_analysis_v2).
const SOURCE_LABELS: readonly [prefix: string, label: string][] = [
  ['fundamentals:', 'Fundamentaldaten'],
  ['price:', 'Kurs'],
  ['ai_analysis:news:', 'News'],
  ['ai_analysis:highlight:', 'News'],
  ['ai_analysis:catalysts:', 'News'],
  ['ai_analysis:analysts_analysis', 'Analysten'],
  ['ai_analysis:market_analyst_sentiment:', 'Analysten'],
  ['ai_analysis:earnings', 'Earnings'],
  ['ai_analysis:competition:', 'Wettbewerb'],
  ['ai_analysis:general:', 'Profil'],
];

export function usedSourceLabels(response: ChatResponse): string[] {
  const labels: string[] = [];
  for (const source of response.sources) {
    if (!source.used) continue;
    const label = SOURCE_LABELS.find(([prefix]) => source.id.startsWith(prefix))?.[1];
    if (label && !labels.includes(label)) labels.push(label);
  }
  return labels;
}

@Injectable({ providedIn: 'root' })
export class V3ChatService {
  private readonly assetsStore = inject(AssetsStore);
  private readonly authStore = inject(AuthStore);
  private readonly authBridge = inject(AuthBridgeService);
  private readonly themeService = inject(ThemeService);
  private readonly flags = inject(FeatureFlagStore);
  private readonly snowplow = inject(SnowplowService);
  private readonly isGuestMode = v3GuestMode(this.themeService, this.authStore);

  // Zero themes only for now.
  readonly isAvailable = computed(
    () => this.flags.isChatbotEnabled() && !this.themeService.isFinnet() && !this.isGuestMode(),
  );
  readonly isOpen = signal(false);
  // Desktop web's wide reading mode; kept across close/reopen.
  readonly isExpanded = signal(false);
  readonly exchanges = signal<ChatExchange[]>([]);
  readonly pending = computed(() => this.exchanges().some((x) => x.status === 'pending'));
  readonly companyName = computed(() => this.assetsStore.v2Data()?.name ?? '');

  // Focus returns here on close.
  private opener: HTMLElement | null = null;
  private nextId = 1;
  private transcriptIsin: string | null = null;

  open(opener?: HTMLElement | null): void {
    if (!this.isAvailable()) return;
    this.syncTranscriptIsin();
    this.opener = opener ?? null;
    if (this.isOpen()) return;
    this.isOpen.set(true);
    this.track('element_clicked', 'ai_chat_open');
  }

  close(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.opener?.focus();
    this.opener = null;
  }

  toggleExpanded(): void {
    const expanded = !this.isExpanded();
    this.isExpanded.set(expanded);
    this.track('element_clicked', expanded ? 'ai_chat_expand' : 'ai_chat_collapse');
  }

  // One question at a time; resolves once the answer or error is in the transcript.
  async ask(rawQuestion: string, source: ChatQuestionSource): Promise<void> {
    const question = rawQuestion.trim().slice(0, CHAT_MAX_QUESTION_CHARS);
    const isin = this.syncTranscriptIsin();
    if (!question || !isin || this.pending()) return;

    const id = this.nextId++;
    this.exchanges.update((list) => [
      ...list,
      {
        id,
        question,
        source,
        status: 'pending',
        response: null,
        html: '',
        error: null,
      },
    ]);
    this.track('element_clicked', 'ai_chat_question', { source });

    const started = performance.now();
    try {
      const response = await askAssetQuestion(isin, question, {
        authBridge: this.authBridge,
        authStore: this.authStore,
      });
      this.patch(id, { status: 'done', response, html: renderChatAnswer(response.answer) });
      this.track('element_viewed', 'ai_chat_answer', {
        question_type: response.question_type,
        provider: response.usage.provider,
        duration_ms: Math.round(performance.now() - started),
      });
    } catch (error) {
      const kind: ChatErrorKind = error instanceof ChatRequestError ? error.kind : 'failed';
      this.patch(id, { status: 'error', error: kind });
      this.track('element_viewed', 'ai_chat_error', { kind });
    }
  }

  retry(id: number): Promise<void> {
    const exchange = this.exchanges().find((x) => x.id === id);
    if (!exchange || exchange.status !== 'error' || this.pending()) return Promise.resolve();
    this.exchanges.update((list) => list.filter((x) => x.id !== id));
    return this.ask(exchange.question, exchange.source);
  }

  // A new ISIN starts a new transcript; an answer still in flight for the old one patches nothing.
  // For a derivative the analyses belong to the underlying stock (v2Data.isin), so the chat asks about that.
  private syncTranscriptIsin(): string | null {
    const isin = this.assetsStore.v2Data()?.isin ?? this.assetsStore.isin();
    if (isin !== this.transcriptIsin) {
      this.transcriptIsin = isin;
      this.exchanges.set([]);
    }
    return isin;
  }

  private patch(id: number, changes: Partial<ChatExchange>): void {
    this.exchanges.update((list) => list.map((x) => (x.id === id ? { ...x, ...changes } : x)));
  }

  private track(
    kind: 'element_clicked' | 'element_viewed',
    title: string,
    property?: Record<string, string | number | boolean>,
  ): void {
    const params = {
      element_title: title,
      feature_name: 'ai_chat',
      isin: this.assetsStore.isin() ?? undefined,
      property,
    };
    const event =
      kind === 'element_clicked'
        ? makeEvent('element_clicked', params)
        : makeEvent('element_viewed', params);
    void this.snowplow.trackerReady().then(() => this.snowplow.track(event));
  }
}
